import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import { useWorkspace } from '../../../utils/WorkspaceContext';
import api from '../../../services/api';
import NarrativeSections from '../BiWeeklyReport/NarrativeSections';

/** ISO date string (yyyy-mm-dd) for a Date, in local time. */
const isoDate = (date) => {
  const offset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 10);
};

/**
 * Per-day descriptive notes — the daily counterpart of the biweekly report,
 * carrying the same narrative fields so a fortnight of notes can be rolled up
 * into the review with one click ("Pull daily notes" on the biweekly page).
 *
 * The day picker creates the note lazily: the first edit on a date POSTs it.
 */
const DailyNotes = () => {
  const { currentWorkspace } = useWorkspace();

  const today = isoDate(new Date());

  const [date, setDate] = useState(today);
  const [note, setNote] = useState(null);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);

  // The trailing fortnight, so the sidebar mirrors one biweekly period.
  const rangeStart = isoDate(new Date(Date.now() - 13 * 86400000));

  const loadRecent = useCallback(async () => {
    if (!currentWorkspace?.id) return;
    try {
      const { data } = await api.get('daily-notes/', {
        params: { workspace_id: currentWorkspace.id, start: rangeStart, end: today },
      });
      setRecent(data);
    } catch {
      setRecent([]);
    }
  }, [currentWorkspace, rangeStart, today]);

  const loadNote = useCallback(async () => {
    if (!currentWorkspace?.id) return;
    setLoading(true);
    try {
      const { data } = await api.get('daily-notes/', {
        params: { workspace_id: currentWorkspace.id, date },
      });
      // No row yet for this day: show an empty draft rather than an error.
      setNote(data[0] || {
        id: null, date, priorities: '', accomplishments: [],
        issues_roadblocks: '', review_reflection: '', supervisor_comments: '',
      });
    } catch {
      setNote(null);
    }
    setLoading(false);
  }, [currentWorkspace, date]);

  useEffect(() => { loadNote(); }, [loadNote]);
  useEffect(() => { loadRecent(); }, [loadRecent]);

  /** Create the note on first edit, then PATCH it thereafter. */
  const save = async (payload) => {
    try {
      const { data } = note?.id
        ? await api.patch(`daily-notes/${note.id}/`, payload)
        : await api.post('daily-notes/', {
          workspace: currentWorkspace.id, date, ...payload,
        });
      setNote(data);
      loadRecent();
    } catch (err) {
      setToast(err?.response?.data?.detail || 'Could not save that change.');
    }
  };

  if (!currentWorkspace?.id) {
    return <Alert severity="info" sx={{ m: 2 }}>Pick a workspace to write daily notes.</Alert>;
  }

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ mb: 0.5 }}>Daily Notes</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Jot the day down as it happens; the biweekly report can pull a whole
        period's notes in at once.
      </Typography>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <TextField
              type="date"
              size="small"
              fullWidth
              label="Date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <Button
              size="small"
              variant="outlined"
              disabled={date === today}
              onClick={() => setDate(today)}
            >
              Today
            </Button>
          </Box>
          <Paper variant="outlined">
            <Typography variant="subtitle2" sx={{ px: 2, pt: 1.5 }}>
              Last 14 days
            </Typography>
            <List dense>
              {recent.length === 0 && (
                <ListItemText
                  sx={{ px: 2, py: 1 }}
                  secondary="No notes written yet."
                />
              )}
              {recent.map((entry) => (
                <ListItemButton
                  key={entry.id}
                  selected={entry.date === date}
                  onClick={() => setDate(entry.date)}
                >
                  <ListItemText
                    primary={entry.date}
                    secondary={
                      entry.accomplishments?.length
                        ? `${entry.accomplishments.length} accomplishment(s)`
                        : 'Notes only'
                    }
                  />
                </ListItemButton>
              ))}
            </List>
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 9 }}>
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          )}
          {!loading && note && (
            <>
              <Box sx={{ mb: 1 }}>
                <Chip
                  size="small"
                  label={note.id ? 'Saved' : 'Not written yet'}
                  color={note.id ? 'success' : 'default'}
                />
              </Box>
              <NarrativeSections
                report={note}
                // The server hands back the same capability block the biweekly
                // report does, so the shared editor gates its fields the same
                // way. An unsaved draft is always the caller's own to write.
                permissions={note.permissions || { can_edit_employee_fields: true }}
                onChange={save}
              />
            </>
          )}
        </Grid>
      </Grid>

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        message={toast}
      />
    </Box>
  );
};

export default DailyNotes;
